import React from 'react';
import { shape, string, func, bool, arrayOf, oneOf, instanceOf, node } from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { FormattedTime, injectIntl, intlShape, defineMessages } from 'react-intl';
import { removeNotification } from '../notifications/actions';
import Button from '../button';
import Icon from '../icon';
import Spinner from '../spinner';

import {
  TYPE_ERROR,
  TYPE_SUCCESS,
  TYPE_PENDING,
} from './types';

// Re-export for backward compatibility
export {
  TYPE_ERROR,
  TYPE_SUCCESS,
  TYPE_PENDING,
};

export const messages = defineMessages({
  success: {
    id: 'notification.label.success',
    defaultMessage: 'Success',
  },
  failure: {
    id: 'notification.label.fail',
    defaultMessage: 'Failure',
  },
  pending: {
    id: 'notification.label.pending',
    defaultMessage: 'In progress',
  },
  close: {
    id: 'notification.button.close',
    defaultMessage: 'Close',
  },
  dismiss: {
    id: 'notification.button.dismiss',
    defaultMessage: 'Dismiss',
  },
  retry: {
    id: 'notification.button.retry',
    defaultMessage: 'Retry',
  },
  reloadPage: {
    id: 'notification.button.reloadPage',
    defaultMessage: 'Reload page',
  },
});

export const icons = {
  success: 'check_circle',
  failure: 'report_problem',
  dismiss: 'close',
  retry: 'refresh',
  reloadPage: 'autorenew',
  viewPage: 'open_in_new',
};

const defaultNotification = {
  title: '',
  text: '',
  type: TYPE_SUCCESS,
  autoHide: true,
};

export const createNotification = (notification) => {
  const datetime = new Date();

  return {
    datetime,
    key: Number(datetime),
    ...defaultNotification,
    ...notification,
  };
};

const NotificationType = oneOf([TYPE_SUCCESS, TYPE_ERROR, TYPE_PENDING]);

export const NotificationShape = {
  type: NotificationType.isRequired,
  title: node,
  text: node.isRequired,
  autoHide: bool,
  datetime: instanceOf(Date).isRequired,
  showDismiss: bool,
  actions: arrayOf(shape({
    icon: string.isRequired,
    text: string.isRequired,
    onClick: func.isRequired,
  })),
};

const NotificationIcon = ({ type, progress }) => (
  <div className={classnames('mdc-notification-icon', type)}>
    {{
      [TYPE_SUCCESS]: <Icon>check_circle</Icon>,
      [TYPE_ERROR]: <Icon>report_problem</Icon>,
      [TYPE_PENDING]: <Spinner progress={progress} />,
    }[type]}
  </div>
);

NotificationIcon.propTypes = {
  type: NotificationType.isRequired,
};

const getDefaultTitle = (type, intl) => intl.formatMessage(
  {
    [TYPE_SUCCESS]: messages.success,
    [TYPE_ERROR]: messages.failure,
    [TYPE_PENDING]: messages.pending,
  }[type],
);

class Notification extends React.PureComponent {
  constructor(props) {
    super(props);

    this.hideNotification = this.hideNotification.bind(this);
    this.state = { timeout: null };
  }

  componentDidMount() {
    this.updateTimer();
  }

  componentDidUpdate(props) {
    this.updateTimer(props);
  }

  updateTimer(props) {
    const { autoHide } = this.props.notification;
    const TIMEOUT = 10000;

    if (!props || autoHide !== props.notification.autoHide) {
      if (autoHide) {
        this.setState({
          timeout: setTimeout(
            () => this.hideNotification(),
            TIMEOUT,
          ),
        });
      } else if (this.state.timeout) {
        clearTimeout(this.state.timeout);
        this.setState({ timeout: null });
      }
    }
  }

  hideNotification() {
    this.props.closeNotification(this.props.notification);
  }

  renderButton(action) {
    const onClick =
      () => {
        if (action.onClick(this.hideNotification) !== false) {
          this.hideNotification();
        }
      };

    return (
      <Button className="mdc-button mdc-notification-action" onClick={onClick} key={action.text}>
        <span>
          <Icon className="mdc-notification-action-icon">{action.icon}</Icon>
          {action.text}
        </span>
      </Button>
    );
  }

  render() {
    const { notification, intl } = this.props;
    let actions = [];

    if (notification && notification.actions && notification.actions.length) {
      let notificationActions = [];

      if (notification.showDismiss !== false) {
        notificationActions.push({
          icon: icons.dismiss,
          text: intl.formatMessage(messages.dismiss),
          onClick: () => {
            this.hideNotification();

            return false;
          },
        });
      }

      notificationActions = notificationActions.concat(notification.actions);
      actions = notificationActions.map(action => this.renderButton(action));
    }

    return (
      notification && (
        <div className="mdc-notification">
          <div className="mdc-notification-content">
            <NotificationIcon type={notification.type} progress={notification.progress} />
            <div className="mdc-notification-content">
              <div className="mdc-notification-headline">
                <div className="mdc-notification-title">
                  {notification.title || getDefaultTitle(notification.type, intl)}
                </div>
                <div className="mdc-notification-time">
                  <FormattedTime value={notification.datetime} />
                </div>
                <div className="mdc-notification-close">
                  <Icon
                    tabIndex={0}
                    aria-label={intl.formatMessage(messages.close)}
                    alt={intl.formatMessage(messages.close)}
                    role="button"
                    onClick={this.hideNotification}
                  >
                    close
                  </Icon>
                </div>
              </div>
              <div className="mdc-notifications-text">
                {notification.text}
              </div>
              {
                actions.length
                  ? (<div className="mdc-notification-actions">{actions}</div>)
                  : ''
              }
            </div>
          </div>
        </div>
      )
    );
  }
}

Notification.propTypes = {
  notification: shape(NotificationShape).isRequired,
  intl: intlShape.isRequired,
  closeNotification: func,
};

Notification.defaultProps = {
  closeNotification: () => {
  },
};

export default connect(
  null,
  dispatch => ({
    closeNotification: notification => dispatch(removeNotification(notification)),
  }),
)(injectIntl(Notification));
